import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { 
  CheckCircle, XCircle, Clock, Pin, Lock, Unlock, 
  MessageCircle, Eye, Filter, ChevronDown, Settings, Image, Trash2
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminModerationPage = () => {
  const { user } = useAuth();
  const [pendingPosts, setPendingPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedPost, setSelectedPost] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending_approval');
  const [permissions, setPermissions] = useState(null);
  const [savingPermissions, setSavingPermissions] = useState(false);
  // Phase: admin delete/remove control
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);   // post_id awaiting confirm in card
  const [modalDeleteConfirm, setModalDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'league_admin';

  useEffect(() => {
    fetchPosts();
    fetchPermissions();
  }, [filterStatus]);

  const fetchPermissions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/community-permissions`, { withCredentials: true });
      setPermissions(res.data);
    } catch (error) {
      // Set defaults
      setPermissions({
        require_approval: { parent: true, coach: false, volunteer: true },
        can_post: { parent: true, coach: true, volunteer: false },
        can_upload_images: { parent: true, coach: true, volunteer: false }
      });
    }
  };

  const handleSavePermissions = async () => {
    setSavingPermissions(true);
    try {
      await axios.put(`${API_URL}/api/admin/community-permissions`, permissions, { withCredentials: true });
      alert('Community permissions saved!');
    } catch (error) {
      console.error('Failed to save permissions:', error);
      alert('Failed to save permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const togglePermission = (category, role) => {
    setPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [role]: !prev[category][role]
      }
    }));
  };

  const fetchPosts = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/community/moderation/queue`, { withCredentials: true }),
        axios.get(`${API_URL}/api/community/moderation/queue?status=${filterStatus}`, { withCredentials: true })
      ]);
      setPendingPosts(pendingRes.data);
      setAllPosts(allRes.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (postId) => {
    try {
      await axios.post(`${API_URL}/api/community/posts/${postId}/approve`, {}, { withCredentials: true });
      fetchPosts();
      setSelectedPost(null);
    } catch (error) {
      console.error('Failed to approve:', error);
      alert(error.response?.data?.detail || 'Failed to approve post');
    }
  };

  const handleReject = async (postId) => {
    try {
      await axios.post(`${API_URL}/api/community/posts/${postId}/reject`, {
        note: rejectionNote
      }, { withCredentials: true });
      fetchPosts();
      setSelectedPost(null);
      setRejectionNote('');
    } catch (error) {
      console.error('Failed to reject:', error);
      alert(error.response?.data?.detail || 'Failed to reject post');
    }
  };

  const handleTogglePin = async (postId) => {
    try {
      await axios.post(`${API_URL}/api/community/posts/${postId}/pin`, {}, { withCredentials: true });
      fetchPosts();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  const handleToggleLockComments = async (postId) => {
    try {
      await axios.post(`${API_URL}/api/community/posts/${postId}/lock-comments`, {}, { withCredentials: true });
      fetchPosts();
    } catch (error) {
      console.error('Failed to toggle comments:', error);
    }
  };

  // Phase: admin permanently removes a post (status -> archived). The community feed
  // already filters to status=published, so archived posts disappear from the live feed.
  const handleDelete = async (postId) => {
    if (deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/community/posts/${postId}`, { withCredentials: true });
      setDeleteConfirmId(null);
      setModalDeleteConfirm(false);
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Failed to delete:', error);
      alert(error.response?.data?.detail || 'Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-100 text-green-700">Published</Badge>;
      case 'pending_approval':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      case 'archived':
        return <Badge className="bg-gray-100 text-gray-700">Archived</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const PostCard = ({ post, showActions = true }) => (
    <Card className="mb-4" data-testid={`mod-post-${post.post_id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {getStatusBadge(post.status)}
              {post.team_name && (
                <Badge variant="outline">{post.team_name}</Badge>
              )}
              <Badge variant="secondary">{post.post_type}</Badge>
            </div>
            <p className="font-semibold text-navy">{post.author_name}</p>
            <p className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {post.is_pinned && (
              <Pin className="w-4 h-4 text-gold" />
            )}
            {!post.comments_enabled && (
              <Lock className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>

        {post.title && (
          <h3 className="font-bold text-navy mb-2">{post.title}</h3>
        )}
        <p className="text-gray-700 whitespace-pre-wrap mb-4 line-clamp-3">{post.body}</p>

        {showActions && (
          <div className="pt-3 border-t">
            {deleteConfirmId === post.post_id ? (
              <div
                className="flex items-center justify-between gap-3 bg-red/5 border border-red/30 rounded-lg px-3 py-2"
                data-testid={`delete-confirm-bar-${post.post_id}`}
              >
                <p className="text-sm text-red font-medium">
                  Permanently remove this post from the community feed?
                </p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteConfirmId(null)}
                    disabled={deleting}
                    data-testid={`cancel-delete-post-${post.post_id}`}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDelete(post.post_id)}
                    disabled={deleting}
                    className="bg-red hover:bg-red/90 text-white"
                    data-testid={`confirm-delete-post-${post.post_id}`}
                  >
                    {deleting ? 'Removing…' : 'Confirm Delete'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedPost(post)}
                    data-testid={`view-post-${post.post_id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                </div>

                <div className="flex items-center space-x-2">
                  {post.status === 'pending_approval' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(post.post_id)}
                        className="text-red border-red hover:bg-red/5"
                        data-testid={`reject-post-${post.post_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(post.post_id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-post-${post.post_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </>
                  )}

                  {post.status === 'published' && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleTogglePin(post.post_id)}
                        className={post.is_pinned ? 'text-gold' : ''}
                      >
                        <Pin className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleLockComments(post.post_id)}
                      >
                        {post.comments_enabled ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      </Button>
                    </>
                  )}

                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeleteConfirmId(post.post_id)}
                      className="text-red border-red/50 hover:bg-red/5"
                      data-testid={`delete-post-${post.post_id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const PostDetailModal = () => {
    if (!selectedPost) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h2 className="text-lg font-bold text-navy">Post Details</h2>
            <Button variant="ghost" size="sm" onClick={() => { setSelectedPost(null); setModalDeleteConfirm(false); }}>
              <XCircle className="w-5 h-5" />
            </Button>
          </div>

          <div className="p-4">
            <div className="flex items-center space-x-2 mb-4">
              {getStatusBadge(selectedPost.status)}
              <Badge variant="secondary">{selectedPost.post_type}</Badge>
              <Badge variant="outline">{selectedPost.feed_type}</Badge>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-500">Author</p>
              <p className="font-medium">{selectedPost.author_name} ({selectedPost.author_role})</p>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-500">Created</p>
              <p>{format(new Date(selectedPost.created_at), 'PPpp')}</p>
            </div>

            {selectedPost.team_name && (
              <div className="mb-4">
                <p className="text-sm text-gray-500">Team</p>
                <p>{selectedPost.team_name}</p>
              </div>
            )}

            {selectedPost.title && (
              <div className="mb-4">
                <p className="text-sm text-gray-500">Title</p>
                <p className="font-bold text-lg">{selectedPost.title}</p>
              </div>
            )}

            <div className="mb-4">
              <p className="text-sm text-gray-500">Content</p>
              <div className="bg-gray-50 rounded-lg p-4 mt-1">
                <p className="whitespace-pre-wrap">{selectedPost.body}</p>
              </div>
            </div>

            {selectedPost.moderation_note && (
              <div className="mb-4 bg-red-50 rounded-lg p-3">
                <p className="text-sm font-medium text-red-700">Rejection Note:</p>
                <p className="text-red-600">{selectedPost.moderation_note}</p>
              </div>
            )}

            {selectedPost.status === 'pending_approval' && (
              <div className="border-t pt-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rejection note (optional)
                  </label>
                  <Textarea
                    value={rejectionNote}
                    onChange={(e) => setRejectionNote(e.target.value)}
                    placeholder="Reason for rejection..."
                    rows={2}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => handleReject(selectedPost.post_id)}
                    className="flex-1 text-red border-red hover:bg-red/5"
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                  <Button
                    onClick={() => handleApprove(selectedPost.post_id)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Approve
                  </Button>
                </div>
              </div>
            )}

            {/* Phase: admin permanent delete from post detail modal */}
            {isAdmin && (
              <div className="border-t pt-4 mt-4">
                {modalDeleteConfirm ? (
                  <div
                    className="bg-red/5 border border-red/30 rounded-lg p-3"
                    data-testid="modal-delete-confirm-bar"
                  >
                    <p className="text-sm font-medium text-red mb-3">
                      Permanently remove this post from the community feed?
                    </p>
                    <div className="flex space-x-3">
                      <Button
                        variant="ghost"
                        onClick={() => setModalDeleteConfirm(false)}
                        disabled={deleting}
                        className="flex-1"
                        data-testid="modal-delete-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => handleDelete(selectedPost.post_id)}
                        disabled={deleting}
                        className="flex-1 bg-red hover:bg-red/90 text-white"
                        data-testid="modal-delete-confirm"
                      >
                        {deleting ? 'Removing…' : 'Confirm Delete'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setModalDeleteConfirm(true)}
                    className="w-full text-red border-red/50 hover:bg-red/5"
                    data-testid="modal-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Post
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-moderation-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-navy mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Community Moderation
          </h1>
          <p className="text-gray-600">Review and manage community posts</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className={pendingPosts.length > 0 ? 'border-yellow-400' : ''}>
            <CardContent className="py-4 text-center">
              <Clock className={`w-6 h-6 mx-auto mb-2 ${pendingPosts.length > 0 ? 'text-yellow-500' : 'text-gray-400'}`} />
              <p className="text-2xl font-bold text-navy">{pendingPosts.length}</p>
              <p className="text-xs text-gray-600">Pending Review</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold text-green-600">
                {allPosts.filter(p => p.status === 'published').length}
              </p>
              <p className="text-xs text-gray-600">Published</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <XCircle className="w-6 h-6 mx-auto mb-2 text-red-400" />
              <p className="text-2xl font-bold text-red">
                {allPosts.filter(p => p.status === 'rejected').length}
              </p>
              <p className="text-xs text-gray-600">Rejected</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b pb-2">
          <Button
            variant={activeTab === 'pending' ? 'default' : 'ghost'}
            onClick={() => { setActiveTab('pending'); setFilterStatus('pending_approval'); }}
            className={activeTab === 'pending' ? 'bg-navy' : ''}
          >
            Pending ({pendingPosts.length})
          </Button>
          <Button
            variant={activeTab === 'all' ? 'default' : 'ghost'}
            onClick={() => { setActiveTab('all'); setFilterStatus('published'); }}
            className={activeTab === 'all' ? 'bg-navy' : ''}
          >
            Published
          </Button>
          <Button
            variant={activeTab === 'rejected' ? 'default' : 'ghost'}
            onClick={() => { setActiveTab('rejected'); setFilterStatus('rejected'); }}
            className={activeTab === 'rejected' ? 'bg-navy' : ''}
          >
            Rejected
          </Button>
          <Button
            variant={activeTab === 'permissions' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('permissions')}
            className={activeTab === 'permissions' ? 'bg-navy' : ''}
            data-testid="permissions-tab"
          >
            <Settings className="w-4 h-4 mr-1" /> Permissions
          </Button>
        </div>

        {/* Posts list */}
        {activeTab === 'pending' && (
          pendingPosts.length > 0 ? (
            pendingPosts.map((post) => (
              <PostCard key={post.post_id} post={post} />
            ))
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                <h3 className="text-xl font-semibold text-navy mb-2">All Caught Up!</h3>
                <p className="text-gray-600">No posts pending review.</p>
              </CardContent>
            </Card>
          )
        )}

        {activeTab !== 'pending' && activeTab !== 'permissions' && (
          allPosts.length > 0 ? (
            allPosts.map((post) => (
              <PostCard key={post.post_id} post={post} />
            ))
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-navy mb-2">No Posts</h3>
                <p className="text-gray-600">No posts with this status.</p>
              </CardContent>
            </Card>
          )
        )}

        {/* Permissions Panel */}
        {activeTab === 'permissions' && permissions && (
          <Card data-testid="permissions-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-navy">
                <Settings className="w-5 h-5" />
                Community Posting Permissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-6">
                Control which roles can post, require approval, and upload images in the community feed. Changes apply immediately.
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-navy">Permission</th>
                      <th className="text-center py-3 px-4 font-semibold text-navy">Parents</th>
                      <th className="text-center py-3 px-4 font-semibold text-navy">Coaches</th>
                      <th className="text-center py-3 px-4 font-semibold text-navy">Volunteers</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium">Can Post</div>
                        <div className="text-xs text-gray-500">Allow this role to create community posts</div>
                      </td>
                      {['parent', 'coach', 'volunteer'].map(role => (
                        <td key={role} className="text-center py-3 px-4">
                          <button
                            onClick={() => togglePermission('can_post', role)}
                            className={`w-10 h-6 rounded-full transition-colors relative ${
                              permissions.can_post?.[role] ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            data-testid={`toggle-can-post-${role}`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                              permissions.can_post?.[role] ? 'right-1' : 'left-1'
                            }`} />
                          </button>
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium">Requires Approval</div>
                        <div className="text-xs text-gray-500">Posts need admin review before publishing</div>
                      </td>
                      {['parent', 'coach', 'volunteer'].map(role => (
                        <td key={role} className="text-center py-3 px-4">
                          <button
                            onClick={() => togglePermission('require_approval', role)}
                            className={`w-10 h-6 rounded-full transition-colors relative ${
                              permissions.require_approval?.[role] ? 'bg-yellow-500' : 'bg-gray-300'
                            }`}
                            data-testid={`toggle-require-approval-${role}`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                              permissions.require_approval?.[role] ? 'right-1' : 'left-1'
                            }`} />
                          </button>
                        </td>
                      ))}
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium flex items-center gap-1">
                          <Image className="w-4 h-4" /> Can Upload Images
                        </div>
                        <div className="text-xs text-gray-500">Allow image uploads in community posts</div>
                      </td>
                      {['parent', 'coach', 'volunteer'].map(role => (
                        <td key={role} className="text-center py-3 px-4">
                          <button
                            onClick={() => togglePermission('can_upload_images', role)}
                            className={`w-10 h-6 rounded-full transition-colors relative ${
                              permissions.can_upload_images?.[role] ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            data-testid={`toggle-can-upload-${role}`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                              permissions.can_upload_images?.[role] ? 'right-1' : 'left-1'
                            }`} />
                          </button>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 flex justify-between items-center pt-4 border-t">
                <p className="text-xs text-gray-500">
                  Admins always have full posting privileges without approval.
                </p>
                <Button 
                  onClick={handleSavePermissions}
                  disabled={savingPermissions}
                  className="bg-navy hover:bg-navy/90"
                  data-testid="save-permissions-btn"
                >
                  {savingPermissions ? 'Saving...' : 'Save Permissions'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Post detail modal */}
        <PostDetailModal />
      </div>
    </ParentLayout>
  );
};

export default AdminModerationPage;
